import { useQuery } from 'react-query';
import type { UseQueryOptions } from 'react-query';

import type {
  DemandContractFormData,
  DemandContractsQuery,
  PortalDemandContract,
  PortalDemandContractCreateData,
  SampleDemandContractFinancialsQuery,
  SampleDemandContractFinancialsResponse,
  ServiceDemandContractCreateData,
} from '@portal/api-models';

import { client } from '../client';
import { QueryKey } from '../types';

interface GetDemandContractsQuery {
  id?: number;
  limit?: number;
  offset?: number;
  subAccountId?: number;
}

const baseUrl = '/finance/demand_contracts';

export const createDemandContract = (data: PortalDemandContractCreateData) =>
  client.post(baseUrl, data).then(({ data }) => data);

export const deleteDemandContract = (id: number) =>
  client.delete<void>(`${baseUrl}/${id}`).then(({ data }) => data);

export const getDemandContracts = (params?: DemandContractsQuery) =>
  client
    .get<PortalDemandContract[]>(baseUrl, { params })
    .then(({ data }) => data);

export const getSampleDemandContractFinancials = (
  url: string,
  body: ServiceDemandContractCreateData
) => {
  return client
    .post<SampleDemandContractFinancialsResponse>(url, body)
    .then(({ data }) => data);
};

export const useGetDemandContractsQuery = (
  params?: GetDemandContractsQuery,
  options?: Omit<
    UseQueryOptions<PortalDemandContract[]>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery(
    [QueryKey.DemandContracts, params],
    async () => {
      const data = await getDemandContracts(params);
      return data;
    },
    options
  );
};

export const useSampleDemandContractFinancials = (
  params?: SampleDemandContractFinancialsQuery,
  body?: DemandContractFormData
) => {
  const isReady = !!body?.effectiveAt && !!body.demandPlatformFeeConfig;

  const urlParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value != null) {
        urlParams.append(key, String(value));
      }
    });
  }

  const queryString = urlParams.toString();
  const url = `${baseUrl}/sampleDemandContractFinancials${
    queryString ? `?${queryString}` : ''
  }`;

  return useQuery(
    [QueryKey.SampleDemandContractFinancials, params, body],
    () => {
      const bodyWithSubAccountId: ServiceDemandContractCreateData = {
        ...body!,
        subAccountId: parseInt(params?.supplySubAccountId || '1'),
      };

      return getSampleDemandContractFinancials(url, bodyWithSubAccountId);
    },
    {
      enabled: isReady,
      keepPreviousData: true,
    }
  );
};
